import {styled} from "@mui/material/styles"
import {ReactNode, type ElementType} from "react"
import {Container, AppBar, Toolbar} from "@mui/material"
import type {ContainerProps} from "@mui/material"
import {paletteColors} from "@indebted/theme"
import {useLocation, useNavigate} from "react-router-dom"

import {useCustomerInfo} from "../../src/useCustomerInfo"

import {InDebted} from "./InDebted"

function Layout({children}: {children: ReactNode}) {
	const newLayout = useIsNewLayout()
	const {customerInfo} = useCustomerInfo()
	const navigate = useNavigate()

	return (
		<>
			{!newLayout ? (
				<StyledAppBar position="fixed">
					<CenteredToolbar>
						<InDebted
							variant="inline"
							width="10em"
							onClick={() => {
								if (customerInfo?.SecureCode) {
									navigate(`/${customerInfo?.SecureCode}`)
								}
							}}
						/>
					</CenteredToolbar>
				</StyledAppBar>
			) : null}

			{/* Second toolbar here is a hack necessary to make contents display below, not under, the appbar */}
			{/* https://material-ui.com/components/app-bar/#fixed-placement */}
			{!newLayout ? <Toolbar /> : null}

			<PageContainer
				component="main"
				maxWidth={newLayout ? "md" : "sm"}
				usesNewLayout={newLayout}
				className="indebted-page-container"
			>
				{children}
			</PageContainer>
		</>
	)
}

/**
 * Constructs regex pattern for payment arrangement paths that should use new layout
 * @param pathSegments - Array of path segments to include in the pattern
 * @returns RegExp - The constructed regex pattern for payment arrangement paths
 */
function constructPaymentArrangementPattern(pathSegments: string[]): RegExp {
	const segmentsPattern = pathSegments.join("|")
	return new RegExp(`^\\/\\d\\/payment-arrangement\\/([0-9a-fA-F-]{36})\\/(${segmentsPattern})$`)
}

function useIsNewLayout() {
	const location = useLocation()

	// Check if current path matches X-XXXX-XXXX pattern
	const pathPattern = /^\/[A-Z0-9]-[A-Z0-9]{4}-[A-Z0-9]{4}\/?$/i
	const isSecureCodeLandingPage = pathPattern.test(location.pathname)
	if (isSecureCodeLandingPage) {
		return true
	}

	// Check payment arrangement paths using constructed pattern
	const paymentArrangementPattern = constructPaymentArrangementPattern([
		"setup",
		"status\\/cancelled-by-agent",
		"status\\/cancelled-by-customer",
		"payment-method",
		"payment-in-full",
		"efta",
	])
	return paymentArrangementPattern.test(location.pathname)
}

const CenteredToolbar = styled(Toolbar)`
	justify-content: center;
`

const PageContainer = styled(({usesNewLayout, ...props}: ContainerProps & {usesNewLayout: boolean}) => (
	<Container {...props} />
))<{
	component: ElementType
	usesNewLayout: boolean
}>`
	background-color: ${paletteColors.white};
	padding: 0 !important;
	${(props) =>
		!props.usesNewLayout &&
		`
		    padding: 0 0.75rem !important;
      border-left: 1px solid rgba(0, 0, 0, 0.12);
      border-right: 1px solid rgba(0, 0, 0, 0.12);
    `}
	min-height: 100vh;
`

const StyledAppBar = styled(AppBar)`
	background-color: ${paletteColors.white};
`

export {Layout}
